import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PoMultiselectOption, PoTableAction, PoTableColumn, PoTagType } from '@po-ui/ng-components';

import { resourceTypeLabel } from '../../../core/utils/resource-type-label.util';
import {
  ReviewStatsAttentionItem, ReviewStatsPeriodFilters
} from '../../interfaces/review-stats.interface';
import { ReviewStatsService } from '../../review-stats.service';
import { CATEGORY_OPTIONS, describeCategories } from '../../utils/review-category-label.util';

/** Uma contagem em tag: só entra na célula o que não é zero. */
interface CountTag {
  label: string;
  type: 'danger' | 'warning' | 'info' | 'neutral';
  /** Detalhe ao passar o mouse — nas sugestões, as categorias. */
  title?: string;
}

interface AttentionRow {
  /** Célula "Review": o nome e, embaixo, tipo e data. */
  reviewCell: { title: string; subtitle: string };
  /**
   * Célula "Achados": severidades, sugestões e rejeitados em tags, numa linha só. As
   * categorias ficam no title da tag de sugestões — em tags próprias elas repetiam o total
   * de sugestões e davam duas ou três linhas por review.
   */
  findingsCell: { tags: CountTag[] };
  review: string;
  type: string;
  requestedAt: string;
  high: number;
  medium: number;
  low: number;
  suggestions: number;
  categories: string;
  rejected: number;
  verdict: string;
  resourceType: string;
  resourceId: string;
}

/** Quantas categorias a célula mostra; as demais viram "+N". */
const CATEGORY_LIMIT = 3;

/**
 * "Reviews que pedem ação": os reviews do recorte com ação obrigatória ALTA ou arquivo
 * REJEITADO, mais recentes primeiro, com atalho para o detalhe.
 *
 * Os cards descrevem; esta lista é o caminho do número até a correção. A unidade é o
 * review, e não o arquivo agregado de vários reviews: o ranking de fontes responde "onde
 * o código é frágil", esta lista responde "o que eu tenho de corrigir hoje".
 */
@Component({
  selector: 'app-review-stats-attention',
  templateUrl: './review-stats-attention.component.html',
  styleUrls: ['./review-stats-attention.component.css']
})
export class ReviewStatsAttentionComponent implements OnChanges {
  @Input() filters: ReviewStatsPeriodFilters = {};
  /** Avisa antes de navegar, para quem hospeda a grade num modal fechá-lo. */
  @Output() reviewOpened = new EventEmitter<void>();
  /** Altura da grade; dentro do modal a rolagem fica nela, com o cabeçalho fixo. */
  @Input() height?: number;

  page = 1;
  search = '';
  /** Categoria de sugestão e severidade — filtros dedicados, à parte da busca livre. */
  selectedCategories: string[] = [];
  selectedSeverities: string[] = [];
  total = 0;
  undetailed = 0;
  loading = false;
  loadError = false;
  readonly pageSize = 20;
  readonly searchLiterals = {
    search: 'Pesquisar por review, nome ou arquivo',
    clean: 'Limpar pesquisa'
  };
  readonly categoryOptions: PoMultiselectOption[] = CATEGORY_OPTIONS;
  readonly severityOptions: PoMultiselectOption[] = [
    { value: 'ALTA', label: 'Alta' }, { value: 'MEDIA', label: 'Média' }, { value: 'BAIXA', label: 'Baixa' }
  ];
  rows: AttentionRow[] = [];

  /**
   * Três colunas ricas no lugar de dez estreitas: com uma coluna por contagem a grade
   * não cabia nem num modal largo, e cortava justamente o veredito e a ação. As
   * contagens viram tags — só as que não são zero —, e tipo e data vão para baixo do nome.
   */
  readonly columns: PoTableColumn[] = [
    // Review e Achados sem largura própria deixavam a tabela crescer com o conteúdo (até
    // 5 tags em Achados) até sobrar espaço nenhum pra coluna de ação — "Abrir review"
    // ficava fora da área visível do modal em várias resoluções (mesma causa do ranking
    // de fontes). Achados quebra linha (ver .css), então não precisa de tanto quanto Review.
    { property: 'reviewCell', label: 'Review', type: 'columnTemplate', width: '28%' },
    { property: 'findingsCell', label: 'Achados', type: 'columnTemplate', width: '32%' },
    {
      property: 'verdict', label: 'Veredito', type: 'label', width: '170px',
      labels: [
        { value: 'REJEITADO', label: 'Rejeitado', type: PoTagType.Danger },
        { value: 'AJUSTES_NECESSARIOS', label: 'Ajustes necessários', type: PoTagType.Warning },
        { value: 'APROVADO', label: 'Aprovado', type: PoTagType.Success },
        { value: 'INSUFICIENTE', label: 'Resposta inválida', type: PoTagType.Neutral },
        { value: '', label: '—', type: PoTagType.Neutral }
      ]
    }
  ];

  readonly actions: PoTableAction[] = [
    { label: 'Abrir review', icon: 'po-icon po-icon-eye', action: (row: AttentionRow) => this.open(row) }
  ];

  private requestSequence = 0;

  constructor(private statsService: ReviewStatsService, private router: Router) {}

  ngOnChanges(): void {
    this.load(true);
  }

  get hasData(): boolean {
    return this.rows.length > 0;
  }

  get allRowsVisible(): boolean {
    return this.rows.length >= this.total;
  }

  /** Ajuda das colunas: o que cada contagem soma, e por que a lista inclui um review. */
  readonly columnHelp =
    '• Alta, Média e Baixa: ações obrigatórias de cada severidade, somadas nos arquivos do review.\n' +
    '• Sugestões: melhorias sugeridas; passe o mouse na tag para ver as categorias.\n' +
    '• Arquivos rejeitados: arquivos com veredito Rejeitado.\n' +
    '• Veredito: o mais severo entre os arquivos do review (Rejeitado, depois Ajustes necessários). ' +
    'É a decisão do assistente sobre cada arquivo, e não sai da contagem de severidades: um review ' +
    'com muitas ações Alta pode vir com Ajustes necessários.\n' +
    '• Entra na lista o review com ao menos uma ação Alta ou um arquivo rejeitado.\n' +
    '• A pesquisa procura pelo review, pelo nome e pelo caminho de qualquer arquivo analisado.\n' +
    '• Categoria e Severidade filtram pela categoria de qualquer sugestão e pela severidade de qualquer ' +
    'ação do review — nenhum dos dois muda quem entra na lista, só reduzem, e casam com QUALQUER valor ' +
    'marcado, não só esses: marcar Média e Baixa não exclui quem também tem Alta, só exige pelo menos uma ' +
    'das duas. Por isso filtrar só por Alta tende a não reduzir nada (quase todo review da lista já tem ' +
    'Alta), e filtrar por Média/Baixa tende a continuar trazendo a maioria — a minoria sem Alta nenhuma ' +
    'é rara, quase sempre entrou pelo arquivo rejeitado.';

  get contextLabel(): string {
    const count = this.total.toLocaleString('pt-BR');
    return this.total === 1
      ? '1 review com ação ALTA ou arquivo rejeitado, mais recente primeiro.'
      : `${count} reviews com ação ALTA ou arquivo rejeitado, mais recentes primeiro.`;
  }

  /** Review sem severidade não é review sem ALTA: a lista diz que ele ficou de fora. */
  get undetailedLabel(): string {
    if (!this.undetailed) { return ''; }
    return this.undetailed === 1
      ? '1 review do recorte ainda não tem a severidade detalhada e não entra na lista.'
      : `${this.undetailed.toLocaleString('pt-BR')} reviews do recorte ainda não têm a severidade detalhada e não entram na lista.`;
  }

  /** A busca roda no backend, antes da paginação e dos totais — o mesmo do ranking. */
  searchReviews(value: string): void {
    this.search = (value ?? '').trim();
    this.load(true);
  }

  categoriesChanged(values: string[]): void {
    this.selectedCategories = values;
    this.load(true);
  }

  severitiesChanged(values: string[]): void {
    this.selectedSeverities = values;
    this.load(true);
  }

  showMore(): void {
    if (this.loading || this.allRowsVisible) { return; }
    this.page++;
    this.load(false);
  }

  open(row: AttentionRow): void {
    this.reviewOpened.emit();
    this.router.navigate(['/menu/smart-review/reviews', row.resourceType, row.resourceId]);
  }

  private load(reset: boolean): void {
    if (reset) {
      this.page = 1;
      this.rows = [];
    }
    this.loading = true;
    this.loadError = false;
    const request = ++this.requestSequence;
    this.statsService.getAttention({
      ...this.filters, search: this.search, category: this.selectedCategories,
      severity: this.selectedSeverities, page: this.page, pageSize: this.pageSize
    }).subscribe({
      next: result => {
        if (request !== this.requestSequence) { return; }
        const incoming = result.items.map(item => this.toRow(item));
        this.rows = reset ? incoming : [...this.rows, ...incoming];
        this.total = result.total;
        this.undetailed = result.undetailed;
        this.loading = false;
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        if (!reset) { this.page--; }
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  /** As principais categorias e quantas mais existem — a lista inteira não cabe na célula. */
  private mainCategories(categories: Record<string, number> | undefined): string {
    const present = Object.values(categories ?? {}).filter(count => count > 0).length;
    const main = describeCategories(categories, CATEGORY_LIMIT);
    if (!main) { return '—'; }
    return present > CATEGORY_LIMIT ? `${main} +${present - CATEGORY_LIMIT}` : main;
  }

  private toRow(item: ReviewStatsAttentionItem): AttentionRow {
    const type = resourceTypeLabel(item.resourceType) || item.resourceType;
    const requestedAt = formatDateTime(item.requestedAt);
    const count = (n: number, one: string, many: string) => `${n.toLocaleString('pt-BR')} ${n === 1 ? one : many}`;
    const tags: CountTag[] = [];
    if (item.severityHigh) { tags.push({ label: count(item.severityHigh, 'Alta', 'Alta'), type: 'danger' }); }
    if (item.severityMedium) { tags.push({ label: count(item.severityMedium, 'Média', 'Média'), type: 'warning' }); }
    if (item.severityLow) { tags.push({ label: count(item.severityLow, 'Baixa', 'Baixa'), type: 'info' }); }
    if (item.suggestions) {
      tags.push({
        label: count(item.suggestions, 'Sugestão', 'Sugestões'), type: 'neutral',
        title: describeCategories(item.categories)
      });
    }
    if (item.rejectedFiles) {
      tags.push({ label: count(item.rejectedFiles, 'Arquivo rejeitado', 'Arquivos rejeitados'), type: 'danger' });
    }
    return {
      reviewCell: { title: item.label || item.resourceId, subtitle: `${type} · ${requestedAt}` },
      findingsCell: { tags },
      review: item.label || item.resourceId,
      type: resourceTypeLabel(item.resourceType) || item.resourceType,
      requestedAt: formatDateTime(item.requestedAt),
      high: item.severityHigh,
      medium: item.severityMedium,
      low: item.severityLow,
      suggestions: item.suggestions,
      categories: this.mainCategories(item.categories),
      rejected: item.rejectedFiles,
      verdict: item.worstVerdict ?? '',
      resourceType: item.resourceType,
      resourceId: item.resourceId
    };
  }
}

/** RFC 3339 em UTC → data e hora locais, `dd/mm/aaaa hh:mm`. */
function formatDateTime(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) { return raw || '—'; }
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}
